// Stable, gently tilted clusters. Small collections keep a loose ring; larger
// ones fill a disc, with collision clearance based on each image's display size.
import { accessoryCategory, accessoryScale, categoryLabel } from './accessory-categories.js';
export { accessoryScale } from './accessory-categories.js';

export const CLUSTER_ITEM_SIZE = 220;
export const CLUSTER_ITEM_GAP = 12;
export const CLUSTER_LABEL_RADIUS = 92;
export const CLUSTER_LABEL_GAP = 24;
export const CLUSTER_GAP = 360;
export const SUBCLUSTER_GAP = 40;

function seedFor(key) {
    let seed = 0;
    for (const c of key) seed = (seed * 31 + c.charCodeAt(0)) >>> 0;
    return seed;
}

export function accessoryDimensions(item = {}) {
    const size = CLUSTER_ITEM_SIZE * accessoryScale(item);
    const aspect = item.img_w > 0 && item.img_h > 0 ? item.img_w / item.img_h : 1;
    return { width: aspect > 1 ? size : size * aspect,
        height: aspect > 1 ? size / aspect : size };
}

export function circularSlots(itemsOrCount, key = '') {
    const dimensions = Array.isArray(itemsOrCount)
        ? itemsOrCount.map(accessoryDimensions)
        : Array.from({ length: itemsOrCount }, () => ({ width: CLUSTER_ITEM_SIZE, height: CLUSTER_ITEM_SIZE }));
    const count = dimensions.length;
    if (!count) return { radius: 0, points: [] };
    const phase = (seedFor(key) % 360) * Math.PI / 180;
    const radii = dimensions.map(({ width, height }) => Math.hypot(width, height) / 2);
    const maxRadius = Math.max(...radii);
    const meanRadius = radii.reduce((sum, r) => sum + r, 0) / count;
    const pitch = meanRadius * 2 + CLUSTER_ITEM_GAP;
    const ringRadius = Math.max(220, CLUSTER_LABEL_RADIUS + maxRadius + CLUSTER_LABEL_GAP,
        count > 1 ? (maxRadius * 2 + CLUSTER_ITEM_GAP) / (2 * Math.sin(Math.PI / count)) : 0);
    const tilt = phase * 0.5 + 0.35;
    const points = dimensions.map((size, i) => {
        const organic = count > 5;
        const angle = phase + i * (organic ? Math.PI * (3 - Math.sqrt(5)) : Math.PI * 2 / count);
        const radius = organic
            ? Math.sqrt((CLUSTER_LABEL_RADIUS + meanRadius + 16) ** 2 + i * (pitch * 0.59) ** 2)
            : ringRadius;
        const x = Math.cos(angle) * radius * (organic ? 1.12 : 1);
        const y = Math.sin(angle) * radius * (organic ? 0.94 : 1);
        return { ...size, x: x * Math.cos(tilt) - y * Math.sin(tilt),
            y: x * Math.sin(tilt) + y * Math.cos(tilt) };
    });

    if (count > 5) {
        const anchors = points.map(p => ({ x: p.x, y: p.y }));
        // Relax overlapping image discs while retaining the sunflower's varied
        // distances from the centre and its slight inclination. No live physics
        // or random reshuffling is needed on resize, zoom or repeat visits.
        for (let step = 0; step < 100; step++) {
            for (let i = 0; i < count; i++) {
                const a = points[i];
                if (step < 70) {
                    a.x += (anchors[i].x - a.x) * 0.025;
                    a.y += (anchors[i].y - a.y) * 0.025;
                }
                for (let j = i + 1; j < count; j++) {
                    const b = points[j];
                    const dx = b.x - a.x, dy = b.y - a.y;
                    const distance = Math.hypot(dx, dy);
                    const clearance = radii[i] + radii[j] + CLUSTER_ITEM_GAP;
                    if (distance >= clearance) continue;
                    const push = (clearance - distance) / (2 * distance);
                    a.x -= dx * push; a.y -= dy * push;
                    b.x += dx * push; b.y += dy * push;
                }
                const distance = Math.hypot(a.x, a.y);
                const clearance = CLUSTER_LABEL_RADIUS + radii[i] + CLUSTER_LABEL_GAP;
                if (distance < clearance) { a.x *= clearance / distance; a.y *= clearance / distance; }
            }
        }

        // A final uniform expansion closes any remaining numerical/relaxation
        // gaps, guaranteeing clearance even for unusually large collections.
        let expansion = 1;
        for (let i = 0; i < count; i++) {
            const a = points[i];
            expansion = Math.max(expansion,
                (CLUSTER_LABEL_RADIUS + radii[i] + CLUSTER_LABEL_GAP) / Math.hypot(a.x, a.y));
            for (let j = i + 1; j < count; j++) {
                const b = points[j];
                expansion = Math.max(expansion, (radii[i] + radii[j] + CLUSTER_ITEM_GAP)
                    / Math.hypot(a.x - b.x, a.y - b.y));
            }
        }
        for (const p of points) { p.x *= expansion; p.y *= expansion; }
    }
    return { radius: Math.max(...points.map((p, i) => Math.hypot(p.x, p.y) + radii[i])), points };
}

function bounds(circles) {
    return {
        left: Math.min(...circles.map(c => c.x - c.radius)),
        right: Math.max(...circles.map(c => c.x + c.radius)),
        top: Math.min(...circles.map(c => c.y - c.radius)),
        bottom: Math.max(...circles.map(c => c.y + c.radius)),
    };
}

function relaxDiscs(circles, gap, centerRadius = 0, centerGap = 0) {
    const anchors = circles.map(c => {
        const factor = centerRadius ? (centerRadius + c.radius + centerGap) / Math.hypot(c.x, c.y) : 1;
        return { x: c.x * factor, y: c.y * factor };
    });
    for (let step = 0; step < 160; step++) {
        for (let i = 0; i < circles.length; i++) {
            const a = circles[i];
            if (step < 110) {
                a.x += (anchors[i].x - a.x) * 0.035;
                a.y += (anchors[i].y - a.y) * 0.035;
            }
            for (let j = i + 1; j < circles.length; j++) {
                const b = circles[j], dx = b.x - a.x, dy = b.y - a.y;
                const distance = Math.hypot(dx, dy), clearance = a.radius + b.radius + gap;
                if (distance >= clearance) continue;
                const push = (clearance - distance) / (2 * distance);
                a.x -= dx * push; a.y -= dy * push;
                b.x += dx * push; b.y += dy * push;
            }
            if (centerRadius) {
                const distance = Math.hypot(a.x, a.y), clearance = centerRadius + a.radius + centerGap;
                if (distance < clearance) { a.x *= clearance / distance; a.y *= clearance / distance; }
            }
        }
    }
    let expansion = 1;
    circles.forEach((a, i) => {
        if (centerRadius) expansion = Math.max(expansion, (centerRadius + a.radius + centerGap) / Math.hypot(a.x, a.y));
        for (const b of circles.slice(i + 1)) {
            expansion = Math.max(expansion, (a.radius + b.radius + gap) / Math.hypot(a.x - b.x, a.y - b.y));
        }
    });
    for (const c of circles) { c.x *= expansion; c.y *= expansion; }
}

// Category clusters have no empty label hole: even a single item sits at the
// centre, and larger sets pack into small filled circles.
function compactSlots(items, key) {
    const circles = items.map((item, index) => {
        const size = accessoryDimensions(item);
        return { ...size, index, radius: Math.hypot(size.width, size.height) / 2, x: 0, y: 0 };
    });
    const phase = seedFor(key) % 360 * Math.PI / 180;
    const pitch = circles.reduce((sum, c) => sum + c.radius * 2, 0) / circles.length + CLUSTER_ITEM_GAP;
    circles.forEach((circle, i) => {
        const angle = phase + i * Math.PI * (3 - Math.sqrt(5));
        const distance = Math.sqrt(i) * pitch * 0.59;
        circle.x = Math.cos(angle) * distance;
        circle.y = Math.sin(angle) * distance;
    });
    relaxDiscs(circles, CLUSTER_ITEM_GAP);
    const box = bounds(circles);
    for (const c of circles) { c.x -= (box.left + box.right) / 2; c.y -= (box.top + box.bottom) / 2; }
    return { points: circles, radius: Math.max(...circles.map(c => Math.hypot(c.x, c.y) + c.radius)) };
}

// Distribute circles around a fixed centre. Angular space is weighted by their
// radii, so a large subgroup gets enough room without pushing everything into
// a row. A final clearance pass covers non-adjacent pairs too.
function placeOrbit(circles, centerRadius, gap, key, centerGap = gap) {
    if (!circles.length) return;
    const anglesFor = padding => circles.map((a, i) => {
        const b = circles[(i + 1) % circles.length];
        const da = centerRadius + a.radius + centerGap + padding;
        const db = centerRadius + b.radius + centerGap + padding;
        const required = a.radius + b.radius + gap;
        return Math.acos(Math.max(-1, Math.min(1, (da * da + db * db - required * required) / (2 * da * db))));
    });
    let padding = 0;
    if (anglesFor(0).reduce((sum, a) => sum + a, 0) > Math.PI * 2) {
        let lo = 0, hi = Math.max(...circles.map(c => c.radius)) * circles.length + gap;
        for (let step = 0; step < 40; step++) {
            const mid = (lo + hi) / 2;
            if (anglesFor(mid).reduce((sum, a) => sum + a, 0) > Math.PI * 2) lo = mid;
            else hi = mid;
        }
        padding = hi;
    }
    const angles = anglesFor(padding);
    const spare = (Math.PI * 2 - angles.reduce((sum, a) => sum + a, 0)) / circles.length;
    let angle = (seedFor(key) % 360) * Math.PI / 180 - Math.PI / 2;
    circles.forEach((circle, i) => {
        const distance = centerRadius + circle.radius + centerGap + padding;
        circle.x = Math.cos(angle) * distance;
        circle.y = Math.sin(angle) * distance;
        angle += angles[i] + spare;
    });
    let expansion = 1;
    circles.forEach((a, i) => {
        for (const b of circles.slice(i + 1)) {
            expansion = Math.max(expansion, (a.radius + b.radius + gap) / Math.hypot(a.x - b.x, a.y - b.y));
        }
    });
    for (const c of circles) { c.x *= expansion; c.y *= expansion; }
}

export function groupedSlots(items, key = '') {
    if (!items.length) return { radius: 0, points: [], subgroups: [] };
    const categories = new Map();
    items.forEach((item, index) => {
        const category = accessoryCategory(item);
        if (!categories.has(category)) categories.set(category, []);
        categories.get(category).push(index);
    });
    const entries = [...categories].sort(([a], [b]) => a.localeCompare(b));
    // With very few images, keep a loose ring and place like categories next
    // to one another. A single-category season uses the filled season layout.
    if (items.length <= 5 || entries.length === 1) {
        const indices = entries.flatMap(([, indices]) => indices);
        const layout = circularSlots(indices.map(i => items[i]), key);
        const points = [];
        indices.forEach((index, i) => { points[index] = { ...layout.points[i], category: accessoryCategory(items[index]) }; });
        return { ...layout, points, subgroups: entries.map(([key, indices]) => ({ key, label: categoryLabel(key), indices })) };
    }
    const subgroups = entries.map(([category, indices]) => ({ key: category, label: categoryLabel(category), indices,
        ...compactSlots(indices.map(i => items[i]), `${key}:${category}`), x: 0, y: 0 }));
    placeOrbit(subgroups, CLUSTER_LABEL_RADIUS, SUBCLUSTER_GAP, key, CLUSTER_LABEL_GAP);
    // Small categories can settle into gaps nearer the title instead of
    // sharing the large categories' radius and recreating a hollow ring.
    relaxDiscs(subgroups, SUBCLUSTER_GAP, CLUSTER_LABEL_RADIUS, CLUSTER_LABEL_GAP);
    const points = [];
    for (const subgroup of subgroups) {
        subgroup.indices.forEach((index, i) => {
            const point = subgroup.points[i];
            points[index] = { width: point.width, height: point.height,
                x: subgroup.x + point.x, y: subgroup.y + point.y, category: subgroup.key };
        });
    }
    return { points, subgroups,
        radius: Math.max(...subgroups.map(c => Math.hypot(c.x, c.y) + c.radius)) };
}

export function clusterLayout(groups) {
    if (!groups.length) return { groups: [], width: 0, height: 0 };
    const circles = groups.map((g, index) => ({ ...g, index,
        ...(g.items ? groupedSlots(g.items, g.key) : circularSlots(g.count, g.key)), x: 0, y: 0 }));
    const central = [...circles].sort((a, b) => (b.items?.length ?? b.count) - (a.items?.length ?? a.count)
        || b.radius - a.radius || a.index - b.index)[0];
    placeOrbit(circles.filter(c => c !== central), central.radius, CLUSTER_GAP, 'seasons');
    const box = bounds(circles);
    // Symmetric bounds keep the largest season at the exact overview centre,
    // including when neighbouring seasons have unequal sizes.
    const halfW = Math.max(-box.left, box.right), halfH = Math.max(-box.top, box.bottom);
    for (const c of circles) { c.x += halfW; c.y += halfH; }
    return { groups: circles, width: halfW * 2, height: halfH * 2 };
}
